import { useCallback, useMemo, useState } from 'react';
import { DocumentDuplicateIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useVirtualAppointmentTypeLabels } from '../../../../hooks/useVirtualAppointments';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import {
    MAX_PLANNED_VIRTUAL_APPOINTMENTS,
    type VirtualAppointmentDto
} from '../../../../services/virtualAppointments';
import { VirtualAppointmentEditor } from '../../../virtual-appointments/VirtualAppointmentEditor';
import { VirtualAppointmentMasterDetail } from '../../../virtual-appointments/VirtualAppointmentMasterDetail';
import { VirtualAppointmentListTabs } from '../../../virtual-appointments/VirtualAppointmentListTabs';
import {
    VirtualAppointmentDetailPrompt,
    VirtualAppointmentReadOnlyDetail
} from '../../../virtual-appointments/VirtualAppointmentDetailPanels';
import {
    VirtualAppointmentSelectorList,
    type VirtualAppointmentListEntry
} from '../../../virtual-appointments/VirtualAppointmentSelectorList';
import {
    useVirtualAppointmentImageUpload,
    useVirtualAppointmentTypeOptions
} from '../../../virtual-appointments/useVirtualAppointmentEditorSupport';
import { useVirtualAppointmentSelection } from '../../../virtual-appointments/useVirtualAppointmentSelection';
import { useVirtualAppointmentSummaries } from '../../../virtual-appointments/useVirtualAppointmentSummaries';
import { isFrozenSummary } from '@odo/shared-ui/clinical/virtualAppointments/summary';
import { summariesWithKeyFirst } from '../../../virtual-appointments/virtualAppointmentSummary';
import {
    hasDraftErrors,
    type VirtualAppointmentDraftPatch
} from '../../../virtual-appointments/virtualAppointmentDraft';
import { isOpenStatus } from '../../../virtual-appointments/openVirtualAppointments';
import { NO_TREATMENT_START_ON_RECORD } from '../../../virtual-appointments/virtualAppointmentWeeksHint';
import type { VirtualAppointmentPlansState } from './useVirtualAppointmentPlans';

export interface PlannerHistory {
    /** Every VA outside this document, newest first. */
    vas: VirtualAppointmentDto[];
    /** Submitting cancels the planned and open ones (a refinement's first RDI submit). */
    cancelsOnSubmit: boolean;
}

interface ClinicalVirtualAppointmentPlannerProps {
    patientId: string;
    planner: VirtualAppointmentPlansState;
    /** Adds a History tab next to the planned VAs. */
    history?: PlannerHistory;
}

type ListTab = 'planned' | 'history';

const NO_HISTORY: VirtualAppointmentDto[] = [];

function useListEntries(planner: VirtualAppointmentPlansState): VirtualAppointmentListEntry[] {
    const typeLabels = useVirtualAppointmentTypeLabels();
    const summaries = useVirtualAppointmentSummaries(
        planner.frozen,
        planner.planned,
        typeLabels,
        planner.savedTriggers
    );
    const { errorsByKey, convertedLegacyKey } = planner;
    return useMemo(
        () =>
            summariesWithKeyFirst(summaries, convertedLegacyKey).map((summary) => ({
                summary,
                hasErrors: hasDraftErrors(errorsByKey[summary.key] ?? {}),
                removable: !isFrozenSummary(summary)
            })),
        [summaries, convertedLegacyKey, errorsByKey]
    );
}

function useHistoryEntries(vas: VirtualAppointmentDto[]): VirtualAppointmentListEntry[] {
    const summaries = useVirtualAppointmentSummaries(vas);
    return useMemo(
        () => summaries.map((summary) => ({ summary, hasErrors: false, removable: false })),
        [summaries]
    );
}

// The aligner and week series restart, so only their kind carries over; a no-go threshold is
// relative and is kept, when the patient is still linked to DM.
function copyTriggerPatch(
    va: VirtualAppointmentDto,
    dmLinked: boolean
): VirtualAppointmentDraftPatch {
    switch (va.triggerKind) {
        case 'aligner':
        case 'weeks':
            return { triggerKind: va.triggerKind };
        case 'nogos':
            return dmLinked
                ? { triggerKind: 'nogos', triggerNogos: String(va.triggerNogos ?? '') }
                : {};
        case 'now':
            return {};
    }
}

// Type, team, assignee, description and the trigger (see copyTriggerPatch) carry over.
function copyPatch(va: VirtualAppointmentDto, dmLinked: boolean): VirtualAppointmentDraftPatch {
    return {
        typeMode: va.typeCode ? 'code' : 'custom',
        typeCode: va.typeCode ?? '',
        typeCustom: va.typeCustom ?? '',
        team: va.team,
        assigneeEmail: va.assigneeEmail ?? '',
        ...copyTriggerPatch(va, dmLinked),
        ...(va.descriptionDoc && { descriptionDoc: va.descriptionDoc })
    };
}

interface HistoryDetailProps {
    vas: VirtualAppointmentDto[];
    selectedKey: string | null;
    onCopy: (va: VirtualAppointmentDto) => void;
    canCopy: boolean;
}

interface CopyButtonProps {
    onCopy: () => void;
    canCopy: boolean;
}

function CopyToPlanButton({ onCopy, canCopy }: CopyButtonProps) {
    return (
        <button
            type="button"
            onClick={onCopy}
            disabled={!canCopy}
            title={canCopy ? undefined : `Up to ${MAX_PLANNED_VIRTUAL_APPOINTMENTS} VAs`}
            className="flex items-center gap-1.5 bg-white text-gray-800 border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-xs disabled:opacity-50 disabled:cursor-not-allowed"
        >
            <DocumentDuplicateIcon className="w-4 h-4" />
            Copy to plan
        </button>
    );
}

function HistoryDetail({ vas, selectedKey, onCopy, canCopy }: HistoryDetailProps) {
    const va = vas.find((v) => v.id === selectedKey);
    if (va) {
        const action = <CopyToPlanButton onCopy={() => onCopy(va)} canCopy={canCopy} />;
        return <VirtualAppointmentReadOnlyDetail va={va} titleAction={action} />;
    }
    const message =
        vas.length === 0 ? 'No earlier VAs for this patient.' : 'Select a VA to see its details.';
    return <VirtualAppointmentDetailPrompt message={message} />;
}

function ExpireOnSubmitDisclaimer() {
    return (
        <div className="flex-shrink-0 flex gap-2 px-3 py-2 border-b border-yellow-200 bg-yellow-50 text-xs text-yellow-800">
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 text-yellow-600" />
            <p>
                Planned and open VAs from the previous treatment expire when this RDI is submitted.
            </p>
        </div>
    );
}

interface PlannerDetailProps extends ClinicalVirtualAppointmentPlannerProps {
    selectedKey: string | null;
    revealedKeys: ReadonlySet<string>;
    dmLinked: boolean;
}

function PlannerDetail({
    patientId,
    planner,
    selectedKey,
    revealedKeys,
    dmLinked
}: PlannerDetailProps) {
    const typeOptions = useVirtualAppointmentTypeOptions();
    const handleImageUpload = useVirtualAppointmentImageUpload(patientId);
    const frozen = planner.frozen.find((va) => va.id === selectedKey);
    const draft = planner.planned.find((d) => d.key === selectedKey);

    const scope = planner.triggerScope;
    if (frozen) return <VirtualAppointmentReadOnlyDetail va={frozen} />;
    if (!draft) {
        const empty = planner.planned.length === 0 && planner.frozen.length === 0;
        const message = empty ? 'Add a VA to plan it here.' : 'Select a VA to see its details.';
        return <VirtualAppointmentDetailPrompt message={message} />;
    }
    return (
        <VirtualAppointmentEditor
            key={draft.key}
            draft={draft}
            errors={planner.errorsByKey[draft.key] ?? {}}
            typeOptions={typeOptions}
            revealErrors={revealedKeys.has(draft.key)}
            onChange={planner.update}
            onImageUpload={handleImageUpload}
            dmLinked={dmLinked}
            treatmentStartDate={scope?.treatmentStartDate ?? null}
            aligners={scope?.aligners}
            treatmentStartUnavailableHint={
                scope?.weeksUnavailable ? NO_TREATMENT_START_ON_RECORD : undefined
            }
            // A row already counting from now keeps the choice, so it can be switched back.
            offerWeeksFromNow={planner.offerWeeksFromNow || draft.weeksFrom === 'now'}
            savedTriggers={planner.savedTriggers}
            savedNogo={draft.id ? planner.savedNogoCounts.get(draft.id) : undefined}
        />
    );
}

export function ClinicalVirtualAppointmentPlanner({
    patientId,
    planner,
    history
}: ClinicalVirtualAppointmentPlannerProps) {
    const [tab, setTab] = useState<ListTab>('planned');
    // Already loaded by the wizard's form step; no-go triggers need the patient linked to DM.
    const { data: card } = usePatientCard(patientId);
    const dmLinked = card?.monitoring?.dmId != null;
    const historyVas = history?.vas ?? NO_HISTORY;
    const historyEntries = useHistoryEntries(historyVas);
    const historyKeys = useMemo(() => historyVas.map((va) => va.id), [historyVas]);
    const historySelection = useVirtualAppointmentSelection(historyKeys);
    const showHistory = history !== undefined && tab === 'history';
    const cancelsSome =
        history?.cancelsOnSubmit === true &&
        historyVas.some((va) => va.status === 'planned' || isOpenStatus(va.status));
    const entries = useListEntries(planner);
    const keys = useMemo(() => entries.map((e) => e.summary.key), [entries]);
    const { selectedKey, select } = useVirtualAppointmentSelection(
        keys,
        planner.convertedLegacyKey
    );
    // Rows present when the step opened (incl. partial rows from a draft) show errors at once.
    const [revealedKeys] = useState<ReadonlySet<string>>(() => new Set(keys));
    const { add, update } = planner;
    const handleAdd = useCallback(() => {
        const key = add();
        if (key !== null) select(key);
    }, [add, select]);
    const handleCopy = useCallback(
        (va: VirtualAppointmentDto) => {
            const key = add();
            if (key === null) return;
            update(key, copyPatch(va, dmLinked));
            setTab('planned');
            select(key);
        },
        [add, update, select, dmLinked]
    );

    const tabs = [
        { key: 'planned' as const, label: 'Planned', count: entries.length },
        { key: 'history' as const, label: 'History', count: historyVas.length }
    ];

    return (
        <VirtualAppointmentMasterDetail
            list={
                <>
                    {history && (
                        <VirtualAppointmentListTabs tabs={tabs} activeKey={tab} onSelect={setTab} />
                    )}
                    {showHistory && cancelsSome && <ExpireOnSubmitDisclaimer />}
                    {showHistory ? (
                        <VirtualAppointmentSelectorList
                            entries={historyEntries}
                            selectedKey={historySelection.selectedKey}
                            onSelect={historySelection.select}
                            emptyText="No earlier VAs."
                        />
                    ) : (
                        <VirtualAppointmentSelectorList
                            entries={entries}
                            selectedKey={selectedKey}
                            onSelect={select}
                            onRemove={planner.remove}
                            add={{
                                onAdd: handleAdd,
                                canAdd: planner.canAdd,
                                disabledHint: `Up to ${MAX_PLANNED_VIRTUAL_APPOINTMENTS} VAs`
                            }}
                            emptyText="No VAs planned yet."
                        />
                    )}
                </>
            }
            detail={
                showHistory ? (
                    <HistoryDetail
                        vas={historyVas}
                        selectedKey={historySelection.selectedKey}
                        onCopy={handleCopy}
                        canCopy={planner.canAdd}
                    />
                ) : (
                    <PlannerDetail
                        patientId={patientId}
                        planner={planner}
                        selectedKey={selectedKey}
                        revealedKeys={revealedKeys}
                        dmLinked={dmLinked}
                    />
                )
            }
        />
    );
}
